import { useState, useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import { get, find, isEmpty } from "lodash"
import { Popover } from "@mui/material"
import { track } from "helpers/analytics"
import { DATE } from "helpers/shapes"
import { useModels } from "resources/models"
import FeedbackForm from "components/feedback-form"
import useLocalStorageSummaries from "../hooks/useLocalSummaries"
import SummaryTabs from "./tabs"
import { TRACKING_EVENTS, ACTIONS } from "../constants"
import { RESOURCE, SUMMARY, EXTRA_PARAMETERS, BASE } from "../shapes"
import useBaseResource from "../hooks/useBaseResource"
import useAISummaryState from "../hooks/useAISummaryState"
import usePopout from "../hooks/usePopout"
import useSummaryButtons from "../hooks/useSummaryButtons"
import Header from "./header"
import AISummarizerButton from "./summarizer-button"

const EMPTY_OBJECT = {}
const { SIMULATOR_SUMMARY, ANALYZE_FACTORS_SUMMARY, FACTOR_SUMMARY, SECURITY_SUMMARY } = ACTIONS

export default function AISummary({ account, resource, startDate, endDate, period, isIntradayMode, latestDate, modelId, base, generate, addNotification, extraParameters, generatedSummaries, isDashboard }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [currentSummaryAction, setCurrentSummaryAction] = useState(ACTIONS.SIMULATOR_SUMMARY)
  const [showDateRange, setShowDateRange] = useState(false)

  const [models] = useModels()
  const model = find(models, { id: modelId })
  const summaryStateProps = useMemo(() => ({ account, model, resource, startDate, endDate, period, isIntradayMode, latestDate, base, generate, extraParameters, localStorageSummaries: summaries, generatedSummaries, isDashboard }), [account, model, resource, startDate, endDate, period, isIntradayMode, latestDate, base, generate, extraParameters, summaries, generatedSummaries, isDashboard])
  const simulatorSummaryState = useAISummaryState({ ...summaryStateProps, action: SIMULATOR_SUMMARY })
  const analyzeFactorsSummaryState = useAISummaryState({ ...summaryStateProps, action: ANALYZE_FACTORS_SUMMARY })
  const factorSummaryState = useAISummaryState({ ...summaryStateProps, action: FACTOR_SUMMARY })
  const securitySummaryState = useAISummaryState({ ...summaryStateProps, action: SECURITY_SUMMARY })

  const summaryStates = useMemo(() => ({
    [ACTIONS.SIMULATOR_SUMMARY]: simulatorSummaryState,
    [ACTIONS.ANALYZE_FACTORS_SUMMARY]: analyzeFactorsSummaryState,
    [ACTIONS.FACTOR_SUMMARY]: factorSummaryState,
    [ACTIONS.SECURITY_SUMMARY]: securitySummaryState,
  }), [simulatorSummaryState, analyzeFactorsSummaryState, factorSummaryState, securitySummaryState])

  const currentSummaryState = useMemo(() => summaryStates[currentSummaryAction] || EMPTY_OBJECT, [summaryStates, currentSummaryAction])
  const { generateSummary, timestamp, summaryKey, summary, error } = currentSummaryState

  // Get the summary matching summaryKey from localStorage
  const [summaries, setLocalStorageSummary, localStorageError] = useLocalStorageSummaries()
  const localStorageSummary = useMemo(() => get(summaries, summaryKey, null), [summaries, summaryKey])

  const areAnySummariesGenerated = useMemo(() => Object.values(summaryStates).some((state) => !!state?.summary && !state?.error), [summaryStates])
  const areAllSummariesGeneratedOrError = useMemo(() => Object.values(summaryStates).every((state) => !!(state?.summary || state?.error)), [summaryStates])

  const baseWithName = useBaseResource(account, modelId, base, endDate)

  const summaryInputs = useMemo(() => ({ resource, startDate, endDate, model, base: baseWithName }), [resource, startDate, model, endDate, baseWithName])

  const feedbackTrackingProps = useMemo(() => {
    return { direction: localStorageSummary?.feedback, ...summaryInputs }
  }, [localStorageSummary, summaryInputs])

  const isGenerated = useMemo(() => currentSummaryState.summary, [currentSummaryState])

  const summaryButtons = useSummaryButtons({
    isGenerated,
    isLoading,
    thumb: localStorageSummary?.feedback,
    summary,
    error,
    resource,
    summaryKey,
    localStorageSummary,
    localStorageError,
    setLocalStorageSummary,
    generateSummary,
    addNotification,
    action: currentSummaryAction,
    setFeedbackOpen,
  })

  const { popoutButton } = usePopout({ account, summaryKey, summaryInputs, addNotification, localStorageError })

  const headerButtons = useMemo(() => [
    popoutButton,
  ], [popoutButton])

  function handleClick(event) {
    setAnchorEl(anchorEl ? null : event.currentTarget)
    track(TRACKING_EVENTS.OPENED_MULTITAB, { resource })
  }

  const isLoading = useMemo(() => !areAllSummariesGeneratedOrError, [areAllSummariesGeneratedOrError])
  const isPopupOpen = Boolean(anchorEl)
  const title = "Open Summaries"

  const handleClose = useCallback(() => setAnchorEl(null), [])

  if (!isEmpty(resource)) {
    return <div className="modules_ai-summary">
      <AISummarizerButton
        title={title}
        handleClick={handleClick}
        isLoading={isLoading}
        isGenerated={areAnySummariesGenerated}
      />

      <Popover
        id="ai-summary-popover"
        open={isPopupOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        classes={{
          paper: "ai-summary-popover-dashboard",
        }}
        anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
        }}
        transformOrigin={{
        vertical: "top",
        horizontal: "right",
        }}
      >
        <Header {...{ headerButtons, isGenerated, timestamp, resource, model, startDate, showDateRange, endDate, base: baseWithName }} />
        <SummaryTabs {...{ setShowDateRange, summaryButtons, summaryStates, setCurrentSummaryAction, resource, base: baseWithName }} />
      </Popover>
      <FeedbackForm
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        feedbackTrackingProps={feedbackTrackingProps}
        trackingEvent={TRACKING_EVENTS.ADDITIONAL_FEEDBACK}
        getPlaceholderText={(direction) => `Why was this summary ${direction === "positive" ? "helpful" : "unhelpful"}?`}
      />
    </div>
  }
}

AISummary.propTypes = {
  account: PropTypes.string.isRequired,
  resource: RESOURCE,
  startDate: DATE,
  endDate: DATE,
  period: PropTypes.string,
  isIntradayMode: PropTypes.bool,
  latestDate: DATE,
  modelId: PropTypes.string.isRequired,
  base: BASE,
  generate: PropTypes.func.isRequired,
  addNotification: PropTypes.func.isRequired,
  extraParameters: EXTRA_PARAMETERS,
  generatedSummaries: PropTypes.objectOf(SUMMARY),
  isDashboard: PropTypes.bool,
}
